import {ModelBase} from './model-base';
import {
    APIService,
    FacilityDetailedSerializer,
    FacilityEmrImportSettingAccountSerializer,
    FacilityEmrUploadSettingsSerializer,
    FacilityForClientDashboardSerializer,
    FacilityLabImportAccountSerializer,
    FacilityPharmacyImportSettingAccountSerializer,
    FacilityPhysicianEMRImportSettingAccountSerializer,
    FacilitySerializer,
    FacilityUserAuthSerializer,
    FacilityWithImportAccountsSerializer,
    FaxSimpleSerializer,
} from '../@core/api.service';
import {GetterCache} from '../utils/accessor.utils';
import {FormFieldOptionAdditionalInfoIcon} from '../@core/forms/form-field-select';

export type FacilityStatusStatus = 'INACTIVE' | 'SUSPENDED' | 'ACTIVE';

export interface Address {
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    zip?: string;
}

export interface FacilityStatus {
    label: string;
    icon: string;
    colorClass: string;
    status: FacilityStatusStatus;
}

export class Facility extends ModelBase implements FacilityDetailedSerializer, FacilityUserAuthSerializer, FacilityForClientDashboardSerializer {
    id: number;
    name: string;
    created_at: Date;
    size: number;
    timezone: string;
    type: string;
    address: string;
    organization: number;
    users_count: number;
    mac: number;
    import_settings: FacilityEmrImportSettingAccountSerializer;
    lab_import_account: number;
    city: string;
    is_inactive: boolean;
    is_suspended: boolean;
    state: string;
    zip_code: string;
    origin: string;
    from_customer_only: boolean;
    access_permission_type: string;
    phone_number: any;
    // FacilityForClientDashboardSerializer
    access_type?: string;
    facility_agreement?: string;
    nls_groups?: any;
    primary_source?: string;

    static viewSet = APIService.FacilityViewSet;
    static inactiveStatus: FacilityStatus = {
        colorClass: 'danger',
        icon: 'minus-circle',
        label: 'Inactive',
        status: 'INACTIVE',
    };

    static suspendedStatus: FacilityStatus = {
        colorClass: 'warning',
        icon: 'pause-circle',
        label: 'Suspended',
        status: 'SUSPENDED',
    };

    static activeStatus: FacilityStatus = {
        colorClass: 'success',
        icon: 'check',
        label: 'Active',
        status: 'ACTIVE',
    };

    assign(x) {
        super.assign(x);
        if (x.created_at && typeof x.created_at === 'string') x.created_at = new Date(x.created_at);
    }

    @GetterCache() get addressString() {
        return Facility.getAddressString(this);
    }

    get noEmr() {
        return ['ARSANA', 'BST'].includes(this.origin);
    }

    get status() {
        return Facility.getStatus(this);
    }

    static getStatus(facility: FacilitySerializer | FacilityDetailedSerializer) {
        if (facility.is_inactive) return Facility.inactiveStatus;
        if (facility.is_suspended) return Facility.suspendedStatus;
        return Facility.activeStatus;
    }

    static getAddressString(address: Address, multiLine?: boolean): string;
    static getAddressString(address: string, city: string, state: string, zip: string, multiLine?: boolean): string;
    static getAddressString(address: Address | string, cityOrMultiline?: string | boolean, state?: string, zip?: string, multiLine?: boolean): string {
        let city = typeof cityOrMultiline === 'string' ? cityOrMultiline : null;
        multiLine = typeof cityOrMultiline === 'boolean' ? cityOrMultiline : (multiLine ?? true);
        if (typeof address === 'object') {
            city = address.city;
            state = address.state;
            zip = address.zip_code || address.zip;
            address = address.address;
        }

        return `${address || '?'}${multiLine ? '\n' : ', '}${city || '?'}, ${state || '?'} ${zip || '?'}`;
    }

    static getAdditionalInfo(facility: FacilityUserAuthSerializer, includeAdminFields = false) {
        const additionalInfo: (string | FormFieldOptionAdditionalInfoIcon)[] = [];
        if (facility.is_inactive) additionalInfo.push(Facility.inactiveStatus);
        else if (includeAdminFields && facility.is_suspended) additionalInfo.push(Facility.suspendedStatus);
        if (facility.state) additionalInfo.push(facility.state);
        if (includeAdminFields) additionalInfo.push(`#${facility.id}`);
        return additionalInfo;
    }
}

export class FacilityDetails extends Facility implements FacilityWithImportAccountsSerializer {
    actual_hospice_payers_nums_critical_lt: number;
    actual_hospice_payers_nums_optimal_gt: number;
    employee: boolean;
    employee_enabled: boolean;
    emr_upload_account: number;
    facility_lab_import_account: Array<FacilityLabImportAccountSerializer>;
    fax: FaxSimpleSerializer;
    groups: number[];
    he_import_account: number;
    import_professions: any;
    include_secondary_payers_in_ccm_eligibility_calculation: boolean;
    lab_facility_origins: number[];
    lab_import_accounts: number[];
    lab_order_upload_facility_origin: number;
    pharmacy_import_settings: FacilityPharmacyImportSettingAccountSerializer;
    physician_emr_import_settings: FacilityPhysicianEMRImportSettingAccountSerializer;
    place_of_service: string;
    primary_source: string;
    rehospitalization_rate_critical_gt: number;
    rehospitalization_rate_optimal_lt: number;
    require_association_for_superadmins: boolean;
    sftp_upload_account: number;
    sftp_upload_folder: string;
    skilled_patients_beds_critical_lt: number;
    skilled_patients_beds_optimal_gt: number;
    unmatched: boolean;
    unmatched_enabled: boolean;
    upload_reports: any;
    upload_settings: FacilityEmrUploadSettingsSerializer;
    npi_number: string;
    legal_business_name: string;
    no_longer_servicing_clients: number[];

    protected static cache_key = '__assignable_cache_2';
    static viewSet = APIService.FacilityViewSet;
}
